/**
 * The 4-sequence template library (Curiosity, Direct question, Right person,
 * Reconnecting) for card S3. Generated from Marvin's checked source
 * (templated messages for Starter and Pro, delivered to Eli 2026-10-13):
 * every message passed checkBannedContent plus the rulebook and SKILL.md
 * rules. Edit the source and regenerate rather than editing here by hand.
 *
 * {first_name} and {company} are filled per lead when the message is sent.
 * [Slots] such as [who you help] are written once per campaign by the user.
 */

export type TemplateAngleId = 'curiosity' | 'direct' | 'right_person' | 'reconnect'

/** One message per sequence position. pro_fu3 and pro_fu4 are Pro-only. */
export type TemplateMessageKey = 'first_dm' | 'fu1' | 'fu2' | 'pro_fu3' | 'pro_fu4' | 'breakup'

export type TemplateAngle = {
  readonly id: TemplateAngleId
  readonly name: string
  /** When to pick this sequence, shown on its card. */
  readonly when: string
  readonly messages: Readonly<Record<TemplateMessageKey, string>>
}

export const TEMPLATE_ANGLES: readonly TemplateAngle[] = [
  {
    id: 'curiosity',
    name: 'Curiosity',
    when: 'Cold leads you know little about. Opens with a question about their week and reveals the offer slowly.',
    messages: {
      first_dm:
        'Hey {first_name}, you\'re in the kind of role I talk to most, since I work with [who you help].\n\nWhat\'s taking up most of your week at {company} right now?',
      fu1:
        'I asked because I help [who you help] with [the problem you solve].\n\nIf that\'s eating into your week too, there\'s a simpler way to handle it.\n\nWould it help to hear how that works?',
      fu2:
        '{first_name}, here\'s what that looks like in practice...\n\nWe [how you deliver it], so you end up with [the result you deliver].\n\nOpen to a 15min call to see how it would fit {company}?',
      pro_fu3:
        'There\'s a second side to this...\n\n[a second problem you solve] tends to show up once [the problem you solve] is sorted. We cover that part as well.\n\nIf mapping it out for {company} sounds good, grab a time here [your booking link]',
      pro_fu4:
        '{first_name}, here\'s a recent example, since it says more than my description...\n\n[a real client result, or a short description of a typical project]\n\nDoes something like that sound relevant?',
      breakup:
        'Before I close this out, I\'d hate for timing to be the only reason we didn\'t talk.\n\nIf [the area you help with] isn\'t on your plate, who should I reach instead?\n\nOtherwise, my calendar\'s here if you want it [your booking link]\nEither way, good luck with the rest of the quarter.',
    },
  },
  {
    id: 'direct',
    name: 'Direct question',
    when: 'Leads who are clearly the buyer. Says what you work on and asks permission to ask, with no pitch until follow-up 1.',
    messages: {
      first_dm:
        'Hey {first_name}, you\'re probably the right person to ask about this at {company}.\n\nI work with [who you help] on [the area you help with], and every team seems to run it differently. Mind if I ask how you handle it?',
      fu1:
        'The reason I\'m asking...\n\nA lot of [who you help] lose hours to [the problem you solve]. We\'ve built a way around that, and it\'s simpler than it sounds.\n\nWould a short rundown be useful?',
      fu2:
        '{first_name}, here\'s the gist...\n\nWe [how you deliver it]. For most clients, that means [the result you deliver].\n\nCould we grab 15min this week so I can show you how it would look at {company}?',
      pro_fu3:
        'A different angle on this...\n\n[a second problem you solve] is often the part that costs the most, and it\'s the part we take care of.\n\nIf you\'d like to see it applied to {company}, pick a time here [your booking link]',
      pro_fu4:
        '{first_name}, one example in case it helps...\n\n[a real client result, or a short description of a typical project]\n\nIs that the kind of outcome you\'d want more of?',
      breakup:
        'One last note before I go quiet, in case the timing was just off.\n\nIf someone else at {company} owns [the area you help with], point me their way. Otherwise, my calendar\'s open [your booking link]\n\nEither way, good luck this quarter.',
    },
  },
  {
    id: 'right_person',
    name: 'Right person',
    when: 'You know the company is a fit but not who the buyer is. Asks who owns the area, so the lead can answer even if it isn\'t them.',
    messages: {
      first_dm:
        'Hey {first_name}, hoping you can point me in the right direction.\n\nI\'m trying to reach whoever at {company} looks after [the area you help with]. Is that you, or someone else on the team?',
      fu1:
        'In case it\'s you, here\'s why I\'m asking.\n\nI help [who you help] with [the problem you solve]. From the outside, {company} looked like a good fit.\n\nWould it be useful to hear how we approach it?',
      fu2:
        '{first_name}, here\'s how working together usually goes...\n\nWe [how you deliver it], and the end result is [the result you deliver].\n\nWould a 15min call make sense to see if it fits?',
      pro_fu3:
        'Something that might change the picture...\n\n[a second problem you solve] is where [who you help] tend to lose the most time. That\'s the piece we take off their hands.\n\nIf you\'re the one weighing this up, grab a slot here [your booking link]',
      pro_fu4:
        '{first_name}, here\'s a real example...\n\n[a real client result, or a short description of a typical project]\n\nCould something like that work for {company}?',
      breakup:
        'Before I stop pinging your inbox, no worries if now isn\'t the right time.\n\nIf [the area you help with] sits with a colleague, a name would really help. And if it\'s you after all, my calendar\'s here [your booking link]\n\nEither way, good luck with everything at {company}.',
    },
  },
  {
    id: 'reconnect',
    name: 'Reconnecting',
    when: 'People already in your network (imported connections). Warmer opener, no connection request needed.',
    messages: {
      first_dm:
        'Hi {first_name}, how are you? We\'ve been connected for a while but haven\'t really talked.\n\nWhat\'s keeping you busy at {company} these days?',
      fu1:
        'Part of why I asked...\n\nThese days I\'m helping [who you help] with [the area you help with], and you came to mind given your work at {company}.\n\nIs that anywhere on your radar right now?',
      fu2:
        '{first_name}, to give you a clearer picture...\n\nWe [how you deliver it], which gets clients [the result you deliver]. It\'s easier to show than describe.\n\nUp for a 15min catch-up this week?',
      pro_fu3:
        'Another reason this might be timely...\n\n[a second problem you solve] tends to grow as [who you help] scale, and it\'s the piece we handle.\n\nIf you want to talk it through, grab a time here [your booking link]',
      pro_fu4:
        '{first_name}, one recent example in case it\'s relevant...\n\n[a real client result, or a short description of a typical project]\n\nWould that kind of result matter for {company}?',
      breakup:
        'Before I close this out, totally fine if the timing isn\'t right.\n\nIf someone else at {company} handles [the area you help with], I\'d appreciate an intro. And if it\'s useful for you, here\'s my calendar [your booking link]\n\nEither way, good to reconnect.',
    },
  },
]
